import React, { useCallback, useEffect, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import mcpDomainsService from '../../services/mcpDomainsService';
import { MATRIX_USER_ROLES, getRoleLabel } from '../../config/userRoles';
import { notify } from '../../components/common/AppToast';
import { AppButton, AppCheckbox, AppSpinner } from '../../components/ui';
import '../../styles/kkp-planning-page.scss';

/** Matrice rôle × domaine MCP (outils IA). */
const McpDomainsAdminPage = () => {
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleLabels, setRoleLabels] = useState(() => (
    Object.fromEntries(MATRIX_USER_ROLES.map((r) => [r.value, r.label]))
  ));
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mcpDomainsService.getAdminMatrix();
      setCatalog(data.catalog ?? []);
      setRoles(data.roles ?? MATRIX_USER_ROLES.map((r) => r.value));
      const meta = Array.isArray(data.roles_meta) ? data.roles_meta : [];
      setRoleLabels(Object.fromEntries(
        (meta.length ? meta : MATRIX_USER_ROLES).map((r) => [r.value, r.label]),
      ));
      setMatrix(data.matrix ?? {});
    } catch (err) {
      notify.error(err.message || 'Impossible de charger la matrice MCP');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setCell = (role, key, allowed) => {
    setMatrix((prev) => ({
      ...prev,
      [role]: {
        ...(prev[role] ?? {}),
        [key]: allowed,
      },
    }));
  };

  const setRowForAllRoles = (key, allowed) => {
    setMatrix((prev) => {
      const next = { ...prev };
      for (const role of roles) {
        next[role] = { ...(next[role] ?? {}), [key]: allowed };
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await mcpDomainsService.saveAdminMatrix(matrix);
      setMatrix(data.matrix ?? matrix);
      notify.success('Matrice MCP enregistrée. Effet immédiat au prochain catalogue d’outils (~5 min max).');
    } catch (err) {
      notify.error(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Domaines MCP"
        subtitle="Autorisations IA par rôle : un domaine couvre un ou plusieurs outils (RAG, lecture ventes…)"
        actions={(
          <AppButton
            size="sm"
            variant="success"
            icon="mdi:content-save-outline"
            loading={saving}
            onClick={handleSave}
            disabled={loading}
          >
            Enregistrer
          </AppButton>
        )}
      />

      <div className="card kkp-planning-results-card menu-visibility-matrix">
        <div className="card-body p-0">
          {loading ? (
            <AppSpinner />
          ) : (
            <StickyTableResponsive className="menu-visibility-matrix__table-wrap">
              <table className="table table-hover table-sm mb-0 align-middle kkp-iti-list-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 320 }}>Domaine</th>
                    {roles.map((role) => (
                      <th key={role} className="text-center" style={{ minWidth: 90 }}>
                        <span className="d-block text-xs">{roleLabels[role] ?? getRoleLabel(role)}</span>
                      </th>
                    ))}
                    <th className="text-center" style={{ width: 80 }}>Ligne</th>
                  </tr>
                </thead>
                <tbody>
                  {catalog.length === 0 ? (
                    <tr>
                      <td colSpan={roles.length + 2} className="text-center text-secondary-light py-24">
                        Aucun domaine configuré.
                      </td>
                    </tr>
                  ) : catalog.map((item) => (
                    <tr key={item.key}>
                      <td>
                        <div className="fw-semibold">{item.label}</div>
                        <div className="text-secondary-light text-xs">{item.description}</div>
                        <code className="text-xs text-secondary-light">{item.key}</code>
                      </td>
                      {roles.map((role) => (
                        <td key={`${item.key}-${role}`} className="text-center">
                          <AppCheckbox
                            id={`mcp-${item.key}-${role}`}
                            checked={Boolean(matrix[role]?.[item.key])}
                            onChange={(e) => setCell(role, item.key, e.target.checked)}
                            aria-label={`${roleLabels[role] ?? getRoleLabel(role)} — ${item.label}`}
                          />
                        </td>
                      ))}
                      <td className="text-center text-nowrap">
                        <AppButton
                          size="sm"
                          variant="outline-success"
                          icon="mdi:check-all"
                          title="Tout cocher"
                          onClick={() => setRowForAllRoles(item.key, true)}
                        />
                        {' '}
                        <AppButton
                          size="sm"
                          variant="outline-secondary"
                          icon="mdi:close"
                          title="Tout décocher"
                          onClick={() => setRowForAllRoles(item.key, false)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default McpDomainsAdminPage;
